import type { CachedAsset } from './cache/store.js';
import type { Logger } from './log.js';

/**
 * House content (technical-design §11.7: an empty or invalid loop plays house
 * content). decision-log O131 is open — nothing yet says what the house asset is,
 * who supplies it, or how it reaches the cache before the first manifest — so this
 * is an interface with two implementations: one that plays an operator-supplied
 * asset once O131 closes, and a built-in branded card that needs neither network
 * nor cache and guarantees invariant 9 in the meantime.
 */

/** How long one house item holds the screen before the loop re-evaluates. */
export const HOUSE_CONTENT_HOLD_MS = 15_000;

export interface HouseContentSource {
  /** null means "render the built-in branded card". */
  current(): Promise<CachedAsset | null>;
}

export function createBuiltInHouseContent(): HouseContentSource {
  return { current: async () => null };
}

/**
 * Plays an asset the operator supplied, falling back to the built-in card whenever
 * it is not cached yet. It is fetched once and kept; it carries no manifest digest,
 * so it is trusted on the basis of its origin rather than verified.
 */
export function createCachedHouseContent(options: {
  readonly load: () => Promise<CachedAsset | null>;
  readonly logger: Logger;
}): HouseContentSource {
  let cached: CachedAsset | null = null;
  return {
    current: async () => {
      if (cached !== null) return cached;
      try {
        cached = await options.load();
      } catch (error) {
        options.logger.warn('house.load_failed', {
          reason: error instanceof Error ? error.message : String(error),
        });
        cached = null;
      }
      return cached;
    },
  };
}
